import { Injectable, Injector } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { from, Observable, throwError } from 'rxjs';
import { catchError, switchMap, tap } from "rxjs/operators";

import { Router } from '@angular/router';
// import { AuthService } from '../services/auth-service.service';
import { OAuthService } from '../services/oauth.service';
import { LoggerService } from '../services/logger.service';
import { AnalyticsService } from '../services/analytics.service';
import { TranslateService } from '@ngx-translate/core';
import { sqlConflictMessageKey, withSqlConflictMessage } from '../services/utilities/sql-conflict-error';
import { environment } from 'src/environments/environment';

/** Route a signed-in user with no roles is sent to; the API answers every other call with 403 NO_ROLES. */
export const AWAITING_ACCESS_URL = '/awaiting-access';

@Injectable({
  providedIn:"root"
})
export class AuthInterceptorService implements HttpInterceptor {
  private noRolesRedirectInFlight = false;

  constructor(private auth: OAuthService, private router: Router, private injector: Injector) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (req.url.startsWith(environment.apiRoot)) {
      return from(this.auth.getAccessToken()).pipe(
        switchMap(token => {
          const authReq = token
            ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
            : req;
          return next.handle(authReq).pipe(tap(_ => {}, error => {
            const respError = error as HttpErrorResponse;
            if (this.isNoRolesRefusal(respError)) {
              // Expected, not an error: the account has no roles yet. Send the user to the
              // awaiting-access page once instead of logging every refused call.
              this.redirectToAwaitingAccess();
              return;
            }
            if (respError && (respError.status === 401 || respError.status === 403)) {
              if (!token) {
                // A 401/403 on a request we sent WITHOUT a token is the expected symptom of a
                // session-expiry transition (access token already expired, silent renewal not
                // yet complete). These are not actionable — log at debug level so they don't
                // flood analytics during every wake-from-background expiry.
                this.resolveLogger()?.debug(
                  'AuthInterceptor',
                  `HTTP ${respError.status} on unauthenticated ${req.method} ${req.url} — token unavailable (session expiring)`
                );
              } else if (!req.url.includes('/analytics')) {
                // Skip analytics requests to avoid recursive event loops
                this.resolveLogger()?.errorWithContext(
                  'AuthInterceptor',
                  `HTTP ${respError.status} on ${req.method} ${req.url}`,
                  respError
                );
              }
            }
          }), catchError(error => this.withFriendlySqlConflict(error)));
        })
      );
    } else {
      return next.handle(req);
    }
  }

  /** Same error, translated message for the SQL-conflict codes (DEADLOCK_RETRY / COMPANY_NOT_FOUND); nothing is retried. */
  private withFriendlySqlConflict(error: unknown): Observable<never> {
    if (error instanceof HttpErrorResponse && sqlConflictMessageKey(error.error)) {
      try {
        const translate = this.injector.get(TranslateService, null);
        if (translate) return throwError(() => withSqlConflictMessage(error, translate));
      } catch { /* fall through to the original error */ }
    }
    return throwError(() => error);
  }

  private isNoRolesRefusal(err: HttpErrorResponse | null | undefined): boolean {
    return !!err && err.status === 403 && err.error?.code === 'NO_ROLES';
  }

  private redirectToAwaitingAccess(): void {
    if (this.noRolesRedirectInFlight || this.router.url.startsWith(AWAITING_ACCESS_URL)) {
      return;
    }
    this.noRolesRedirectInFlight = true;
    this.resolveAnalytics()?.trackAction('no_roles_redirect', 'auth', 'AuthInterceptor');
    // state tells the awaiting page it was sent here by a refusal, so it must not bounce home on a stale token
    this.router.navigate([AWAITING_ACCESS_URL], { replaceUrl: true, state: { fromNoRoles: true } })
      .catch(() => undefined)
      .finally(() => { this.noRolesRedirectInFlight = false; });
  }

  private resolveAnalytics(): AnalyticsService | null {
    try {
      const analytics = this.injector.get(AnalyticsService, null);
      return typeof analytics?.trackAction === 'function' ? analytics : null;
    } catch {
      return null;
    }
  }

  /**
   * Resolved per call rather than injected — see the same guard in CacheInterceptor.
   * Interceptors are created on the first HTTP request, which may happen while
   * LoggerService is still being constructed; a constructor injection would then capture
   * Angular's CIRCULAR sentinel ({}) permanently, because the cyclic-dependency check that
   * would surface it is `ngDevMode`-only and stripped from production builds.
   */
  private resolveLogger(): LoggerService | null {
    try {
      const logger = this.injector.get(LoggerService, null);
      return typeof logger?.debug === 'function' && typeof logger?.errorWithContext === 'function'
        ? logger
        : null;
    } catch {
      return null;
    }
  }
}
